// Force Bill editor for the Billing Review drill-down.
//
// Per-(client, service) bypass of the firm-wide WIP threshold. Presence-based: a row in
// billing.client_force_bill → bypass is on for that service. 'ALL' means every service;
// while ALL is on the per-service toggles are locked (their rows are kept, so they snap
// back if ALL is turned off). Also hosts the GCC "enforce the $500 threshold" toggle,
// as on Client Billing Configuration.
//
// Every change is STAGED (billingChangeApi.js): it shows as "Pending approval" until the
// Billing Team approves it. Services come from the global `availableServices` list.

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import { useAuth } from '../../auth/AuthContext';
import PendingTag from './PendingTag';
import ConfigToggle from './ConfigToggle';
import {
  submitChange, wasWithdrawn, withPending, withoutPending, pendingForKey, revertsPending,
} from './billingChangeApi';

export default function ForceBillEditor({
  client, period, readOnly, availableServices = [], onOptimisticUpdate, onChanged, onWithdraw,
}) {
  const { email } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(new Set());
  const [savingGcc, setSavingGcc] = useState(false);
  const [gccConfirmOpen, setGccConfirmOpen] = useState(false);   // explainer shown before the GCC threshold goes on

  const forceBill = client.forceBill || [];
  const onSet = new Set(forceBill.map(f => f.service));
  const allOn = onSet.has('ALL');

  const pendingFb = (client.pending || []).filter(p => p.section === 'force_bill');
  const pendingByServ = Object.fromEntries(pendingFb.map(p => [String(p.fieldKey).replace(/^force_bill:/, ''), p]));
  const gccPending = (client.pending || []).find(p => p.section === 'misc' && p.fieldKey === 'gccEnforceThreshold') || null;

  // Optimistic: the toggle and its "Pending approval" tag flip at once. Turning something back to its
  // live value (e.g. switching off a service you just switched on) withdraws the open request.
  async function toggleService(serv, nextOn) {
    if (saving.has(serv)) return;
    const snapshot = forceBill;
    const snapshotPending = client.pending || [];
    const nextList = nextOn
      ? [...forceBill.filter(f => f.service !== serv), { service: serv }]
      : forceBill.filter(f => f.service !== serv);
    const body = { section: 'force_bill', payload: { service: serv, enabled: nextOn } };
    const undo = revertsPending(pendingForKey(client, body.section, null, body.payload), body, email);
    onOptimisticUpdate?.(c => {
      const next = { ...c, forceBill: nextList };
      return undo
        ? withoutPending(next, body.section, null, body.payload)
        : withPending(next, { ...body, summary: `Force bill ${serv}: ${nextOn ? 'on' : 'off'}`, email });
    });
    setSaving(prev => new Set(prev).add(serv));
    try {
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out)
        ? `Force bill ${serv} change withdrawn.`
        : `Force bill ${serv} ${nextOn ? 'on' : 'off'} submitted for Billing Team approval.`);
      onChanged?.();
    } catch (err) {
      onOptimisticUpdate?.(c => ({ ...c, forceBill: snapshot, pending: snapshotPending }));
      toast.error(`Failed to update force bill ${serv}: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSaving(prev => { const n = new Set(prev); n.delete(serv); return n; });
    }
  }

  async function toggleGcc(nextOn) {
    if (savingGcc) return;
    const snapshot = !!client.gccEnforceThreshold;
    const snapshotPending = client.pending || [];
    const body = { section: 'misc', fieldKey: 'gccEnforceThreshold', payload: { value: nextOn } };
    const undo = revertsPending(pendingForKey(client, body.section, body.fieldKey, body.payload), body, email);
    onOptimisticUpdate?.(c => {
      const next = { ...c, gccEnforceThreshold: nextOn };
      return undo
        ? withoutPending(next, body.section, body.fieldKey, body.payload)
        : withPending(next, { ...body, summary: `Enforce $500 WIP threshold (GCC): ${nextOn ? 'on' : 'off'}`, email });
    });
    setSavingGcc(true);
    try {
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out)
        ? 'GCC threshold change withdrawn.'
        : 'GCC threshold setting submitted for Billing Team approval.');
      onChanged?.();
    } catch (err) {
      onOptimisticUpdate?.(c => ({ ...c, gccEnforceThreshold: snapshot, pending: snapshotPending }));
      toast.error(`Failed to update GCC threshold setting: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSavingGcc(false);
    }
  }

  // ALL first, then the service list, then any already-on service the list doesn't know
  // (legacy / deactivated) so it can still be turned off.
  const known = new Set(availableServices);
  const orphans = forceBill.map(f => f.service).filter(s => s !== 'ALL' && !known.has(s));
  const rows = ['ALL', ...availableServices, ...orphans];

  const summaryText = allOn
    ? 'Force-billing ALL services'
    : forceBill.length === 0 ? 'None — defers to WIP threshold'
      : `Force-billing ${forceBill.length} service${forceBill.length === 1 ? '' : 's'}`;
  const serviceList = allOn || !forceBill.length ? null : forceBill.map(f => f.service).join(', ');

  return (
    <>
      <div className="cs-editor">
        <div className="detail-row">
          <span className="detail-label">Status:</span> {summaryText}
        </div>
        {serviceList && (
          <div className="detail-row">
            <span className="detail-label">Services:</span>{' '}
            <span className="bg-children-list" title={serviceList}>{serviceList}</span>
          </div>
        )}
        {pendingFb.map(p => (
          <div key={p.changeId} className="br-pending-row">
            <PendingTag change={p} onWithdraw={onWithdraw} />
            <span className="br-sec-note">{p.summary}</span>
          </div>
        ))}
        {!readOnly && (
          <button className="cs-btn cs-btn--add" onClick={() => setModalOpen(true)}>
            <i className="fas fa-edit" /> Edit Force Bill
          </button>
        )}

        <div className="fb-gcc-section">
          <h5 className="fb-gcc-title">DO NOT FORCE BILL — GCC ONLY</h5>
          <p className="br-sec-note">
            All GCC clients in the automated billing run ignore the WIP threshold by default.
            Turning this on enforces the <strong>$500 WIP threshold</strong> on this client.
          </p>
          <ConfigToggle
            label="Enforce $500 WIP threshold"
            checked={client.gccEnforceThreshold}
            disabled={readOnly}
            saving={savingGcc}
            pending={gccPending}
            onWithdraw={onWithdraw}
            onChange={v => (v ? setGccConfirmOpen(true) : toggleGcc(false))}
          />
        </div>
      </div>

      {/* Turning the threshold ON asks first: it only means something for GCC clients. Off needs no popup. */}
      <Modal
        open={gccConfirmOpen}
        onClose={() => setGccConfirmOpen(false)}
        title="Enforce $500 WIP threshold?"
        className="gcc-confirm-modal"
        footer={(
          <>
            <button className="cs-btn" onClick={() => setGccConfirmOpen(false)}>Cancel</button>
            <button
              className="cs-btn cs-btn--save"
              onClick={() => { setGccConfirmOpen(false); toggleGcc(true); }}
            >
              Continue
            </button>
          </>
        )}
      >
        <p className="nc-description" style={{ marginLeft: 0 }}>
          This setting applies to <strong>GovCon (GCC) clients only</strong>.
        </p>
        <p className="nc-description" style={{ marginLeft: 0 }}>
          By default every client has the <strong>$500 WIP threshold</strong>, but GCC clients ignore it
          unless this toggle is turned on. Only turn it on if {client.clientCode} is a GCC client that should
          be held to the threshold.
        </p>
      </Modal>

      <Modal
        open={modalOpen}
        onClose={() => { if (!saving.size) setModalOpen(false); }}
        title={`Edit Force Bill — ${client.clientCode}`}
        wide
        footer={<button className="cs-btn" onClick={() => setModalOpen(false)} disabled={saving.size > 0}>Done</button>}
      >
        <table className="detail-table cs-editor-table">
          <thead>
            <tr><th>Service</th><th className="cs-actions-col">Force Bill</th></tr>
          </thead>
          <tbody>
            {rows.map(serv => {
              const isAll = serv === 'ALL';
              const disabled = !isAll && allOn;
              const isSaving = saving.has(serv);
              return (
                <tr key={serv}>
                  <td>
                    {isAll ? <strong>ALL</strong> : serv}
                    {isAll && <span className="br-sec-note"> (every service for this client)</span>}
                    <PendingTag change={pendingByServ[serv]} onWithdraw={onWithdraw} />
                  </td>
                  <td className="cs-actions-col">
                    <label className={`tech-fee-toggle ${disabled ? 'disabled' : ''} ${isSaving ? 'saving' : ''}`}>
                      <input
                        type="checkbox"
                        checked={onSet.has(serv)}
                        disabled={disabled || isSaving}
                        onChange={e => toggleService(serv, e.target.checked)}
                      />
                      <span className="tech-fee-slider" />
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {allOn && (
          <p className="nc-description" style={{ marginTop: 8, marginLeft: 0 }}>
            <strong>ALL</strong> is on — individual service toggles are locked. Turn off ALL to edit per service.
          </p>
        )}
      </Modal>
    </>
  );
}
